import React, { useContext, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { classes } from 'typestyle'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { runOperator } from '../../../core/operators'
import { withRouter } from '../../../contexts/with-router'

/* Context ====================================================================================== */
import AppLayout from '../../routes/app/layout'
import { CacheContext } from '../../../contexts/cache-context'
import { NavigationContext } from '../../../contexts/navigation'

/* Hooks ======================================================================================== */
import useFetch from '../../../hooks/use-fetch'

/* Utils ======================================================================================== */
import { get } from '../../../share/find'

/* Components =================================================================================== */
import { Box } from '../../../components/box/box.component'
import { Snippet } from '../../../modules/layouts/snippet'
import { renderForm } from '../../../modules/handlers/render-form'
import { Tabs, Tab } from '../../../components/tab'
import { FormContext } from '../../../components/form'

/* Styles ======================================================================================= */
import { paddingLeftInner, paddingHorizontalInner } from '../../../styles/default/padding'

export default addSnippet(
  'FormView',
  withRouter((props: any) => {
    const location = useLocation()
    const params = useParams()
    const { navigation } = useContext(NavigationContext)

    const apiUrl = `${process.env.REACT_APP_API_URL}${location.pathname}`
    const base = get('payload.base', navigation, 'object')
    const sections = get('payload.base.form.sections', navigation, 'array')
    const fields = get('payload.base.form.fields', navigation, 'array')
    const lookups = get('form.lookups', base, 'array')
    const search = { lookups }

    const [row, $row] = useFetch({}, { url: apiUrl, search })

    const cacheContext = useContext(CacheContext)
    const appLayoutContext = useContext(AppLayout.context)
    const { getOffsetTop = () => 0 } = appLayoutContext || ({} as any)

    useEffect(() => {
      if (params.parentRowId) {
        cacheContext?.$value?.set?.(prev => ({ ...prev, parentId: params.parentRowId }))
      }
    }, [params])

    useEffect(() => {
      if (row.mounted && Object.keys(base).length > 0) {
        $row.get({ url: apiUrl, search }).then(payload => {
          cacheContext?.$value?.set?.(prev => ({ ...prev, row: payload }))
        })
      }
    }, [row.mounted, base])

    const { value } = useContext(FormContext)
    const { form } = value

    return (
      <Snippet>
        <Box baseline="surface" rounded relative>
          {(() => {
            if (sections && sections.length > 0) {
              return (
                <>
                  <Tabs allowIndex={false} topOffset={getOffsetTop()}>
                    {sections.map((section: any = { fields: [] }, key: any) => {
                      const { hidden } = runOperator(section, {
                        row: form,
                      })

                      const parentName = `body.${section.name}`
                      const hasError = Object.keys(form).some(
                        (a: any) => a.includes(parentName) && (form as any)?.[a]?.error,
                      )

                      return (
                        <Tab label={section.label} key={key} hidden={hidden} hasError={hasError}>
                          <Box baseline="none">
                            <Box
                              className={classes(paddingLeftInner, paddingHorizontalInner)}
                              baseline="none"
                            >
                              {section.fields &&
                                section.fields.map((field: any, key2: any) => {
                                  return (
                                    <React.Fragment key={key2}>
                                      {renderForm({
                                        field,
                                        isFromView: true,
                                        topOffset: getOffsetTop() + 36, // Tab Header Height
                                        parentName: `body.${section.name}`,
                                        entryUrl: `${process.env.REACT_APP_API_URL}/app`,
                                      })}
                                    </React.Fragment>
                                  )
                                })}
                            </Box>
                          </Box>
                        </Tab>
                      )
                    })}
                  </Tabs>
                </>
              )
            } else if (fields && fields.length > 0) {
              return (
                <Box className={classes(paddingLeftInner, paddingHorizontalInner)} baseline="none">
                  {fields.map((field: any, key2: any) => {
                    return renderForm({
                      field,
                      parentName: 'body',
                      topOffset: getOffsetTop(),
                      entryUrl: `${process.env.REACT_APP_API_URL}/app`,
                    })
                  })}
                </Box>
              )
            }
          })()}
        </Box>
      </Snippet>
    )
  }),
)
